import { makeObservable, observable, action } from 'mobx';
import { trackEvent } from '@/lib/telemetry';
import type { RootStore } from './RootStore';

const RECENT_KEY = 'agent_studio_recent_commands';
const RECENT_MAX = 8;

export interface RecentItem {
  id: string;          // unique key (e.g. "agent:99", "merchant:7")
  label: string;       // display text
  href?: string;       // optional route
  icon?: string;       // icon name hint
  ts: number;          // timestamp for ordering
}

// Holds open/closed state for Cmd+K and a small recents list. The actual
// search results are computed from sibling stores at render time.

export class CommandPaletteStore {
  @observable isOpen = false;
  @observable recent: RecentItem[] = [];

  constructor(_root: RootStore) {
    void _root;
    makeObservable(this);
    this.hydrate();
  }

  private hydrate(): void {
    try {
      const raw = window.localStorage.getItem(RECENT_KEY);
      if (raw) {
        const parsed = JSON.parse(raw) as RecentItem[];
        if (Array.isArray(parsed)) this.recent = parsed.slice(0, RECENT_MAX);
      }
    } catch {
      // ignore
    }
  }

  private persist(): void {
    try {
      window.localStorage.setItem(RECENT_KEY, JSON.stringify(this.recent));
    } catch {
      // ignore
    }
  }

  @action
  open(): void {
    this.isOpen = true;
    trackEvent('CommandPalette.Opened');
  }

  @action
  close(): void {
    this.isOpen = false;
  }

  @action
  toggle(): void {
    if (this.isOpen) this.close();
    else this.open();
  }

  @action
  recordRecent(item: Omit<RecentItem, 'ts'>): void {
    const existing = this.recent.findIndex((r) => r.id === item.id);
    if (existing !== -1) this.recent.splice(existing, 1);
    this.recent.unshift({ ...item, ts: Date.now() });
    if (this.recent.length > RECENT_MAX) this.recent.length = RECENT_MAX;
    this.persist();
  }
}
